// --- Dependencies -------------------------------------------------------------------------------
import { useMemo } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyOverviewMeta, TyOverviewSubscriptions, TySubscriptionStatus } from "@/types";
import Chart from "@/UI/atoms/Chart";
import type { TyChartColor } from "@/UI/atoms/Chart/presets";
import StatTile from "@/UI/atoms/StatTile";
import { alignToBuckets, buildBuckets, formatBucket, formatMoney, formatNumber, rangeLabel } from "./helpers";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    data: TyOverviewSubscriptions;
    meta: TyOverviewMeta;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * Revenue gets the only full-width chart on the screen. Everything else here is context for it: what it
 * would have been without coupons, what is still owed, and which plan length is carrying it.
 */
export default function MoneySection({ data, meta }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);

    const period = rangeLabel(meta.range, t);

    const revenue = useMemo(() => {
        const buckets = buildBuckets(meta.from, meta.to, meta.unit, data.series[0]?.at);
        return {
            labels: buckets.map((slot) => formatBucket(slot, meta.unit, lang)),
            earned: alignToBuckets(data.series, buckets, meta.unit, (row) => row.revenue),
            discount: alignToBuckets(data.series, buckets, meta.unit, (row) => row.discount),
        };
    }, [data.series, meta.from, meta.to, meta.unit, lang]);

    /**
     * A subscription status is a state, not a category, so it wears the reserved status tones instead of
     * a categorical hue — green is live, amber is waiting, red is lapsed, gray is history. The doughnut
     * is labelled as well as coloured, so the two greens and two grays are never told apart by hue alone.
     */
    const statusColors: Record<string, TyChartColor> = {
        active: "good",
        coming: "green",
        grace: "warn",
        pending: "amber",
        paused: "bad",
        canceled: "muted",
        ended: "faint",
    };

    const money = (value: number) => formatMoney(value, lang, t);

    return (
        <section className="home-section">
            <h2 className="home-section-title">{t.home.moneyTitle}</h2>

            <div className="home-tiles">
                <StatTile label={t.home.revenue} value={money(data.revenue)} hint={t.home.revenueHint} tone="brand" to="/subscriptions" />
                <StatTile label={t.home.gross} value={money(data.gross)} hint={t.home.grossHint} />
                <StatTile label={t.home.discountGiven} value={money(data.discount)} hint={t.home.discountGivenHint} tone="warn" to="/coupons" />
                <StatTile label={t.home.avgTicket} value={money(data.avgTicket)} hint={period} />
                <StatTile label={t.home.subsPaid} value={formatNumber(data.paid, lang)} hint={t.home.subsPaidHint} tone="good" to="/subscriptions" />
                <StatTile
                    label={t.home.pendingMoney}
                    value={money(data.pendingAmount)}
                    hint={t.home.pendingMoneyHint}
                    tone={data.pendingCount > 0 ? "warn" : "neutral"}
                    to="/subscriptions"
                />
            </div>

            <div className="home-card home-card--wide">
                <p className="home-card-title">{t.home.revenueOverTime}</p>
                <Chart
                    kind="area"
                    size="lg"
                    caption={t.home.revenueOverTime}
                    labels={revenue.labels}
                    series={[
                        { label: t.home.seriesRevenue, data: revenue.earned, color: "brand" },
                        { label: t.home.seriesDiscount, data: revenue.discount, color: "amber" },
                    ]}
                    formatValue={money}
                    emptyLabel={t.home.noData}
                />
            </div>

            <div className="home-cards home-cards--pair">
                <div className="home-card">
                    <p className="home-card-title">{t.home.subsByStatus}</p>
                    <Chart
                        kind="doughnut"
                        caption={t.home.subsByStatus}
                        labels={data.byStatus.map((row) => t.enums.subscriptionStatus[row.key as TySubscriptionStatus] ?? row.key)}
                        series={[{ label: t.home.subsByStatus, data: data.byStatus.map((row) => row.value) }]}
                        sliceColors={data.byStatus.map((row) => statusColors[row.key] ?? "muted")}
                        emptyLabel={t.home.noData}
                    />
                </div>

                <div className="home-card">
                    <p className="home-card-title">{t.home.subsByPeriod}</p>
                    <Chart
                        kind="hbar"
                        caption={t.home.subsByPeriod}
                        labels={data.byPeriod.map((row) => t.enums.subscriptionPeriod[row.key as "monthly" | "quarterly" | "annually"] ?? row.key)}
                        series={[{ label: t.home.seriesRevenue, data: data.byPeriod.map((row) => row.revenue), color: "blue" }]}
                        formatValue={money}
                        emptyLabel={t.home.noData}
                    />
                </div>
            </div>
        </section>
    );
}
